import React, { useEffect, useState } from "react";
import { neuralNetworkService, NeuralPathwayFeedback } from "../../lib/firebase";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { useNavigate } from "react-router-dom";

const getRatingColor = (rating: number) => {
  if (rating >= 4) return "bg-green-500";
  if (rating >= 3) return "bg-yellow-500";
  return "bg-red-500";
};

const getConfidenceColor = (confidence: number) => {
  if (confidence >= 0.8) return "bg-green-500";
  if (confidence >= 0.6) return "bg-yellow-500";
  return "bg-red-500";
};

export const AdminNeuralFeedback = () => {
  const [feedbacks, setFeedbacks] = useState<NeuralPathwayFeedback[]>([]);
  const [filteredFeedbacks, setFilteredFeedbacks] = useState<NeuralPathwayFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [ratingFilter, setRatingFilter] = useState<string>("all");
  const [helpfulnessFilter, setHelpfulnessFilter] = useState<string>("all");
  const [selectedFeedback, setSelectedFeedback] = useState<NeuralPathwayFeedback | null>(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const data = await neuralNetworkService.getAllPathwayFeedback();
        setFeedbacks(data);
        setFilteredFeedbacks(data);
      } catch (err: any) {
        setError(err.message || "Failed to load feedback");
      } finally {
        setLoading(false);
      }
    };
    fetchFeedback();
  }, []);

  useEffect(() => {
    let filtered = feedbacks;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(fb => 
        fb.pathwayQuery.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fb.feedback.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fb.pathwayContext.userStage.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fb.pathwayContext.userTarget.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply rating filter
    if (ratingFilter !== "all") {
      const rating = parseInt(ratingFilter);
      filtered = filtered.filter(fb => fb.rating === rating);
    }

    // Apply helpfulness filter
    if (helpfulnessFilter !== "all") {
      const wasHelpful = helpfulnessFilter === "helpful";
      filtered = filtered.filter(fb => fb.wasHelpful === wasHelpful);
    }

    setFilteredFeedbacks(filtered);
  }, [feedbacks, searchTerm, ratingFilter, helpfulnessFilter]);

  const getAverageRating = () => {
    if (feedbacks.length === 0) return 0;
    const total = feedbacks.reduce((sum, fb) => sum + fb.rating, 0);
    return (total / feedbacks.length).toFixed(1);
  };

  const getHelpfulPercentage = () => {
    if (feedbacks.length === 0) return 0;
    const helpful = feedbacks.filter(fb => fb.wasHelpful).length;
    return ((helpful / feedbacks.length) * 100).toFixed(1);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 pt-12 pb-12">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#daed6e] mx-auto mb-4"></div>
          <p className="text-lg font-medium">Loading feedback data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 pt-12 pb-12">
        <Alert className="mb-4 bg-red-50 border-red-200">
          <AlertTitle className="text-red-800">Error</AlertTitle>
          <AlertDescription className="text-red-700">{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 pt-12 pb-12">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-bold text-2xl text-black">Neural Pathway User Feedback</h1>
          <p className="text-gray-600 mt-1">Monitor user feedback and pathway performance</p>
        </div>
        <Button 
          onClick={() => navigate('/aAyuiOcvMq/dashboard')}
          variant="outline"
          className="bg-white hover:bg-gray-50"
        >
          ← Back to Dashboard
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-black">{feedbacks.length}</div>
            <div className="text-sm text-gray-600">Total Feedback</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-black">{getAverageRating()}</div>
            <div className="text-sm text-gray-600">Average Rating</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-black">{getHelpfulPercentage()}%</div>
            <div className="text-sm text-gray-600">Found Helpful</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-black">
              {feedbacks.length > 0 ? (feedbacks.reduce((sum, fb) => sum + fb.stepsCount, 0) / feedbacks.length).toFixed(1) : 0}
            </div>
            <div className="text-sm text-gray-600">Avg Steps per Pathway</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Search</label>
              <Input
                placeholder="Search pathways, feedback..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Rating</label>
              <Select value={ratingFilter} onValueChange={setRatingFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ratings</SelectItem>
                  <SelectItem value="5">5 Stars</SelectItem>
                  <SelectItem value="4">4 Stars</SelectItem>
                  <SelectItem value="3">3 Stars</SelectItem>
                  <SelectItem value="2">2 Stars</SelectItem>
                  <SelectItem value="1">1 Star</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Helpfulness</label>
              <Select value={helpfulnessFilter} onValueChange={setHelpfulnessFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="helpful">Helpful</SelectItem>
                  <SelectItem value="not-helpful">Not Helpful</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button 
                onClick={() => {
                  setSearchTerm("");
                  setRatingFilter("all");
                  setHelpfulnessFilter("all");
                }}
                variant="outline"
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feedback List */}
      {filteredFeedbacks.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-gray-500 text-lg">
              {feedbacks.length === 0 ? "No feedback submitted yet." : "No feedback matches your filters."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredFeedbacks.map((fb) => (
            <Card 
              key={fb.id} 
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setSelectedFeedback(fb)}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="font-medium text-black mb-1">
                      {fb.pathwayQuery.length > 80 
                        ? fb.pathwayQuery.substring(0, 80) + "..." 
                        : fb.pathwayQuery
                      }
                    </h3>
                    <p className="text-sm text-gray-600">
                      Stage: {fb.pathwayContext.userStage} • Target: {fb.pathwayContext.userTarget}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Badge className={getRatingColor(fb.rating)}>
                      {fb.rating}/5
                    </Badge>
                    <Badge className={fb.wasHelpful ? "bg-green-200 text-green-800" : "bg-red-200 text-red-800"}>
                      {fb.wasHelpful ? "Helpful" : "Not Helpful"}
                    </Badge>
                    <Badge className={getConfidenceColor(fb.overallConfidence)}>
                      {(fb.overallConfidence * 100).toFixed(0)}% confidence
                    </Badge>
                  </div>
                </div>
                
                {fb.feedback && (
                  <p className="text-sm text-gray-700 mb-2">
                    "{fb.feedback.length > 150 ? fb.feedback.substring(0, 150) + "..." : fb.feedback}"
                  </p>
                )}
                
                <div className="flex justify-between items-center text-xs text-gray-500">
                  <span>{fb.stepsCount} steps • {new Date(fb.createdAt).toLocaleDateString()}</span>
                  <span>Click to view details</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detailed Feedback Modal */}
      {selectedFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle>Feedback Details</CardTitle>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedFeedback(null)}
                >
                  ×
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Pathway Query</h4>
                <p className="text-gray-700 bg-gray-50 p-3 rounded">{selectedFeedback.pathwayQuery}</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium mb-2">User Context</h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Stage:</strong> {selectedFeedback.pathwayContext.userStage}</div>
                    <div><strong>Target:</strong> {selectedFeedback.pathwayContext.userTarget}</div>
                    <div><strong>Findings:</strong> {selectedFeedback.pathwayContext.userFindings}</div>
                    <div><strong>Extra Context:</strong> {selectedFeedback.pathwayContext.extraContext}</div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2">Feedback Metrics</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span>Rating:</span>
                      <Badge className={getRatingColor(selectedFeedback.rating)}>
                        {selectedFeedback.rating}/5
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>Helpful:</span>
                      <Badge className={selectedFeedback.wasHelpful ? "bg-green-200 text-green-800" : "bg-red-200 text-red-800"}>
                        {selectedFeedback.wasHelpful ? "Yes" : "No"}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>Confidence:</span>
                      <Badge className={getConfidenceColor(selectedFeedback.overallConfidence)}>
                        {(selectedFeedback.overallConfidence * 100).toFixed(0)}%
                      </Badge>
                    </div>
                    <div><strong>Steps:</strong> {selectedFeedback.stepsCount}</div>
                    <div><strong>Date:</strong> {new Date(selectedFeedback.createdAt).toLocaleString()}</div>
                  </div>
                </div>
              </div>
              
              {selectedFeedback.feedback && (
                <div>
                  <h4 className="font-medium mb-2">User Feedback</h4>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded">{selectedFeedback.feedback}</p>
                </div>
              )}
              
              {selectedFeedback.improvements.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Suggested Improvements</h4>
                  <ul className="list-disc ml-6 space-y-1">
                    {selectedFeedback.improvements.map((improvement, idx) => (
                      <li key={idx} className="text-gray-700">{improvement}</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminNeuralFeedback; 